import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { SUPPORTED_LANGUAGES, DEFAULT_LANGUAGE } from "@/utils/languageRouter";
import { useTranslation } from "react-i18next";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  Calendar,
  Eye,
  User,
  ChevronLeft,
  Share2,
  Facebook,
  Twitter,
  Linkedin,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import SEOHead from "@/components/SEOHead";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BlogPostPage = () => {
  const { slug, lang: urlLang } = useParams();
  const { t, i18n } = useTranslation();
  const lang = urlLang || i18n.language;
  const currentLang = SUPPORTED_LANGUAGES.includes(lang) ? lang : DEFAULT_LANGUAGE;
  
  const localizedLink = (path) => {
    const cleanPath = path.startsWith("/") ? path : `/${path}`;
    return `/${currentLang}${cleanPath}`;
  };
  
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Check if user is admin
  const isAdmin = () => {
    const token = localStorage.getItem("token");
    if (!token) return false;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.role === "admin";
    } catch {
      return false;
    }
  };

  useEffect(() => {
    fetchPost();
  }, [slug]);

  const fetchPost = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/blog/${slug}`);
      setPost(res.data);
    } catch (err) {
      console.error("Error fetching post:", err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString(lang === "fr" ? "fr-FR" : "en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const shareUrl = typeof window !== "undefined" ? window.location.href : "";

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center pt-20">
        <h2 className="text-2xl font-semibold mb-4">Article non trouvé</h2>
        <Link to={localizedLink("/blog")}>
          <Button>{t("common.back")}</Button>
        </Link>
      </div>
    );
  }

  // Prepare SEO data
  const seoTitle = `${post.title?.[lang] || post.title?.fr || 'Article'} | XploreSahara`;
  const seoDescription = post.summary?.[lang] || post.summary?.fr || "";
  const seoImage = post.cover_image || post.image_url || "";
  const seoKeywords = post.tags || [];
  const seoUrl = `/blog/${slug}`;
  
  const schemaData = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": post.title?.[lang] || post.title?.fr,
    "description": seoDescription,
    "image": seoImage,
    "author": {
      "@type": "Organization",
      "name": "XploreSahara"
    },
    "publisher": {
      "@type": "Organization",
      "name": "XploreSahara",
      "logo": {
        "@type": "ImageObject",
        "url": "https://saharatrek.org/logo.png"
      }
    },
    "datePublished": post.published_at || post.created_at || "",
    "dateModified": post.updated_at || "",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://saharatrek.org${seoUrl}`
    },
    "keywords": seoKeywords.join(", ")
  };

  return (
    <div className="min-h-screen bg-background pt-20" data-testid="blog-post-page">
      {/* SEO Meta Tags */}
      <SEOHead 
        customTitle={seoTitle}
        customDescription={seoDescription}
        customKeywords={seoKeywords}
        canonicalPath={seoUrl}
        imageUrl={seoImage}
        structuredData={schemaData}
      />

      {/* Hero Image */}
      <div className="relative h-[40vh] md:h-[50vh]">
        <img
          src={post.cover_image || "https://images.unsplash.com/photo-1549221987-25a490f65d34?w=1200"}
          alt={post.title?.[lang] || post.title?.fr}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
        
        {/* Back Button */}
        <Link to={localizedLink("/blog")} className="absolute top-6 left-6 z-10">
          <Button variant="secondary" size="sm" className="gap-2 rounded-full">
            <ChevronLeft className="h-4 w-4" />
            {t("common.back")}
          </Button>
        </Link>

        {/* Title Overlay */}
        <div className="absolute bottom-0 left-0 right-0 p-8 md:p-12">
          <div className="max-w-4xl mx-auto">
            <Badge className="mb-4 bg-white/20 text-white border-white/30">
              {post.category}
            </Badge>
            <h1 className="font-serif text-3xl md:text-5xl font-bold text-white mb-4">
              {post.title?.[lang] || post.title?.fr}
            </h1>
            <div className="flex flex-wrap items-center gap-6 text-white/90">
              <div className="flex items-center gap-2">
                <User className="h-5 w-5" />
                <span>{t("blog.by")} {post.author_name}</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                <span>{formatDate(post.published_at)}</span>
              </div>
              {isAdmin() && (
                <div className="flex items-center gap-2">
                  <Eye className="h-5 w-5" />
                  <span>{post.views || 0} {t("blog.views")}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 md:px-8 py-12">
        {/* Excerpt */}
        <p className="text-xl text-muted-foreground leading-relaxed mb-8 font-medium">
          {post.excerpt?.[lang] || post.excerpt?.fr}
        </p>

        {/* Main Content */}
        <div className="prose prose-lg max-w-none prose-headings:font-serif prose-headings:text-secondary prose-p:text-muted-foreground prose-a:text-primary prose-strong:text-secondary prose-li:text-muted-foreground">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              table: ({ node, ...props }) => (
                <div className="overflow-x-auto my-6">
                  <table className="min-w-full border-collapse border border-border rounded-lg overflow-hidden" {...props} />
                </div>
              ),
              thead: ({ node, ...props }) => (
                <thead className="bg-secondary/10" {...props} />
              ),
              th: ({ node, ...props }) => (
                <th className="border border-border px-4 py-3 text-left font-semibold text-secondary text-sm" {...props} />
              ),
              td: ({ node, ...props }) => (
                <td className="border border-border px-4 py-3 text-muted-foreground text-sm" {...props} />
              ),
              tr: ({ node, ...props }) => (
                <tr className="hover:bg-muted/50 transition-colors" {...props} />
              ),
              img: ({ node, ...props }) => (
                <img className="rounded-lg shadow-md my-6 w-full object-cover" {...props} />
              ),
              blockquote: ({ node, ...props }) => (
                <blockquote className="border-l-4 border-primary pl-4 italic text-muted-foreground my-6 bg-muted/30 py-4 pr-4 rounded-r-lg" {...props} />
              ),
            }}
          >
            {post.content?.[lang] || post.content?.fr || ""}
          </ReactMarkdown>
        </div>

        {/* Tags */}
        {post.tags && post.tags.length > 0 && (
          <div className="mt-12 pt-8 border-t border-border">
            <div className="flex flex-wrap gap-2">
              {post.tags.map((tag, idx) => (
                <Badge key={idx} variant="secondary" className="rounded-full">
                  #{tag}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Share */}
        <div className="mt-12 pt-8 border-t border-border">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <Share2 className="h-5 w-5" />
            {t("blog.sharePost")}
          </h3>
          <div className="flex gap-3">
            <a
              href={`https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button variant="outline" size="icon" className="rounded-full">
                <Facebook className="h-4 w-4" />
              </Button>
            </a>
            <a
              href={`https://twitter.com/intent/tweet?url=${shareUrl}&text=${post.title?.[lang] || post.title?.fr}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button variant="outline" size="icon" className="rounded-full">
                <Twitter className="h-4 w-4" />
              </Button>
            </a>
            <a
              href={`https://www.linkedin.com/shareArticle?mini=true&url=${shareUrl}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button variant="outline" size="icon" className="rounded-full">
                <Linkedin className="h-4 w-4" />
              </Button>
            </a>
          </div>
        </div>

        {/* Author Card */}
        <div className="mt-12 p-8 bg-muted/30 rounded-3xl">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
              <span className="text-primary font-bold text-2xl">
                {post.author_name?.charAt(0)}
              </span>
            </div>
            <div>
              <p className="font-semibold text-lg">{post.author_name}</p>
              <p className="text-muted-foreground">Auteur</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogPostPage;
